import React from 'react';
import s from './Home.module.css';
import CustomCard from '../../components/ui/customCard/CustomCard';
import who_we_are_img from '../../assets/img/who_we_are_img.jpg';
import artboard from '../../assets/img/artboard.png';
import lock from '../../assets/img/lock.png';
import {NavLink, useNavigate} from 'react-router-dom';
import {
    ABOUT_US,
    CARDS,
    COMMERCIAL_BANKING,
    DIGITAL_ASSETS_BANKING,
    OPEN_ACCOUNT,
    PRIVATE_BANKING,
    TRADE_FINANCE,
} from '../../router/Constants';
import {useAppSelector} from '../../hooks/redux';
import MyPopupSendSuccess from '../../components/ui/MyPopupSendSuccess/MyPopupSendSuccess';
import {motion} from 'framer-motion';
import CustomBtnBlue from '../../components/ui/customBtnBlue/CustomBtnBlue';
import classNames from 'classnames';
import Contact from '../contact/Contact';
import Hero from '../../components/hero/Hero';
import {useTranslation} from 'react-i18next';
import {Helmet} from "react-helmet-async";

const Home = () => {
    const navigate = useNavigate();
    const isSuccess: boolean | null = useAppSelector(
        (state) => state.userReducer.isMessageSentSuccess
    );

    const {t, i18n} = useTranslation();

    // 1) Build the cards array using translations from i18n
    const cards = [
        {
            title: t('homePage.cards.privateBanking.title'),
            short: t('homePage.cards.privateBanking.short'),
            description: t('homePage.cards.privateBanking.description'),
            link: PRIVATE_BANKING,
        },
        {
            title: t('homePage.cards.commercialBanking.title'),
            short: t('homePage.cards.commercialBanking.short'),
            description: t('homePage.cards.commercialBanking.description'),
            link: COMMERCIAL_BANKING,
        },
        {
            title: t('homePage.cards.cardServices.title'),
            short: t('homePage.cards.cardServices.short'),
            description: t('homePage.cards.cardServices.description'),
            link: CARDS,
        },
        {
            title: t('homePage.cards.digitalAssets.title'),
            short: t('homePage.cards.digitalAssets.short'),
            description: t('homePage.cards.digitalAssets.description'),
            link: DIGITAL_ASSETS_BANKING,
        },
        {
            title: t('homePage.cards.tradeFinance.title'),
            short: t('homePage.cards.tradeFinance.short'),
            description: t('homePage.cards.tradeFinance.description'),
            link: TRADE_FINANCE,
        },
    ];

    return (
        <div className={s.wrapper}>
            <motion.div
                initial={{opacity: 0, scale: 1}}
                animate={{opacity: 1, scale: 1}}
                transition={{duration: 0.5}}
                className={s.motion_div}
            >
                <Helmet>
                    <title>Wall Street IBU | International Banking & Global Financial Services</title>
                    <meta
                        name="description"
                        content="Wall Street IBU provides secure international banking, current accounts, and
                    global banking transfers for individuals and businesses worldwide."
                    />
                    <meta name="keywords" content="international banking services, global banking solutions"/>
                </Helmet>

                {isSuccess && <MyPopupSendSuccess/>}

                {/* Hero Section */}
                <div className={s.content} dir={i18n.language === 'fas' ? 'rtl' : 'ltr'}>
                    <Hero
                        title_1={t('homePage.heroTitle1')}
                        title_2={t('homePage.heroTitle2')}
                        description={t('homePage.heroDescription')}
                        buttons
                    />
                </div>

                {/* Who we are? */}
                <div className={s.who_we_are}>
                    <div className={s.content}>
                        <div className={s.flex}>
                            <div className={s.left}>
                                <h2 className={s.subtitle}>{t('homePage.whoWeAreTitle')}</h2>
                                <p>
                                    <span className={s.bold}>Wall Street IBU</span>{' '}
                                    {t('homePage.whoWeAreParagraph1')}
                                </p>
                                <p>{t('homePage.whoWeAreParagraph2')}</p>
                                <p>{t('homePage.whoWeAreParagraph3')}</p>
                                <p>{t('homePage.whoWeAreParagraph4')}</p>
                                {/* Use the "learnMoreBtn" or from routes/header if you wish */}
                                <CustomBtnBlue
                                    title={t('homePage.learnMoreBtn')}
                                    onClick={() => navigate(ABOUT_US)}
                                />
                            </div>
                            <div className={s.right}>
                                <img src={who_we_are_img} alt=""/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Services we provide */}
                <div className={s.content}>
                    <div className={s.cards_wrapper}>
                        <div className={s.cards_title}>
                            {t('homePage.servicesWeProvideTitle')}
                        </div>
                        {cards.map((card) => (
                            <div className={s.card_block} key={card.title as string}>
                                <NavLink to={card.link} reloadDocument>
                                    <CustomCard
                                        title={card.title as string}
                                        short={card.short as string}
                                        description={card.description as string}
                                    />
                                </NavLink>
                            </div>
                        ))}
                        <div className={s.btn_card}>
                            <CustomBtnBlue
                                // you can use t('homePage.openAccountBtn') or t('header.openAccount')
                                title={t('header.openAccount')}
                                onClick={() => navigate(OPEN_ACCOUNT)}
                            />
                        </div>
                    </div>
                </div>

                {/* Fast & Secure Payment Method */}
                <div className={s.content}>
                    <div className={s.payment_method}>
                        <div className={s.left}>
                            <img src={lock} alt="lock"/>
                        </div>
                        <div className={s.right}>
                            <h2>{t('homePage.fastPaymentTitle')}</h2>
                            <p>{t('homePage.fastPaymentParagraph')}</p>
                            <CustomBtnBlue
                                title={t('homePage.learnMoreBtn')}
                                onClick={() => navigate(ABOUT_US)}
                            />
                        </div>
                    </div>
                </div>

                {/* Open Account Section */}
                <div className={s.open_account_section}>
                    <div className={s.content} dir='ltr'>
                        <div className={s.inner_content}>
                            <div>
                                <h2>{t('homePage.openAccountSectionHeading')}</h2>
                                <p>{t('homePage.openAccountSectionParagraph')}</p>
                                <CustomBtnBlue
                                    title={t('header.openAccount')}
                                    onClick={() => navigate(OPEN_ACCOUNT)}
                                />
                            </div>
                        </div>
                        <img className={s.artboard} src={artboard} alt="img"/>
                    </div>
                </div>

                {/* Contact Form */}
                <div className={s.contact_form_wrapper}>
                    <div className={s.content}>
                        <Contact/>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Home;
